'use client';

import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';

interface RenameModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentName: string;
  newName: string;
  onNameChange: (name: string) => void;
  onRename: () => void;
  title?: string;
}

const MAX_OUTPUT_NAME_LENGTH = 60;

export function RenameModal({
  isOpen,
  onClose,
  currentName,
  newName,
  onNameChange,
  onRename,
  title = 'Rename',
}: RenameModalProps) {
  const [didInitialFocus, setDidInitialFocus] = useState(false);
  // Extract filename without extension and the extension
  const getFileNameParts = (fileName: string) => {
    if (!fileName) return { nameWithoutExtension: '', extension: '' };

    const lastDotIndex = fileName.lastIndexOf('.');
    if (lastDotIndex > 0) {
      return {
        nameWithoutExtension: fileName.substring(0, lastDotIndex),
        extension: fileName.substring(lastDotIndex),
      };
    }
    return {
      nameWithoutExtension: fileName,
      extension: '',
    };
  };

  const { nameWithoutExtension: currentNameWithoutExt, extension } =
    getFileNameParts(currentName || '');
  const { nameWithoutExtension: newNameWithoutExt } = getFileNameParts(
    newName || '',
  );

  const isNameTooLong = newNameWithoutExt.length > MAX_OUTPUT_NAME_LENGTH;

  const handleInitialFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (didInitialFocus) return;
    const target = e.target as HTMLInputElement;
    setTimeout(() => {
      try {
        const end = target.value.length;
        target.setSelectionRange(end, end);
      } catch {}
      setDidInitialFocus(true);
    }, 0);
  };

  // Reset the initial-focus guard when the dialog is opened
  useEffect(() => {
    if (isOpen) {
      setDidInitialFocus(false);
    }
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold text-gray-900">
            {title}
          </DialogTitle>
        </DialogHeader>
        <div className="py-4 space-y-4">
          <div>
            <label
              htmlFor="new-name"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              New name
            </label>
            <Input
              id="new-name"
              value={newName}
              onChange={(e) => onNameChange(e.target.value)}
              onFocus={handleInitialFocus}
              placeholder="Enter new name"
              className={`w-full ${isNameTooLong ? '!border-red-500 focus-visible:!ring-transparent' : ''}`}
              aria-invalid={isNameTooLong}
            />
            {isNameTooLong && (
              <p className="text-xs mt-1" style={{ color: '#ef4444' }}>
                Output name must be {MAX_OUTPUT_NAME_LENGTH} characters or
                fewer. ({newNameWithoutExt.length}/{MAX_OUTPUT_NAME_LENGTH})
              </p>
            )}
          </div>
        </div>
        <DialogFooter className="flex-row justify-end space-x-2 pt-4">
          <Button variant="outline" onClick={onClose} className="px-4 py-2">
            Cancel
          </Button>
          <Button
            onClick={onRename}
            disabled={
              !newName.trim() || newNameWithoutExt === currentNameWithoutExt || isNameTooLong
            }
            className={`px-4 py-2 ${
              newName.trim() && newNameWithoutExt !== currentNameWithoutExt && !isNameTooLong
                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            Rename
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
